import React from 'react'
import styled from 'styled-components'
import { OfficeKaiinjouhouHenkoutodokeData } from '../../../../types/formData'
import { Heading } from '../../../ui/Heading'
import { Input } from '../../../ui/Input'
import { RadioButtonLabel } from '../../../ui/RadioButtonLabel'
import { Controller, useFormContext } from 'react-hook-form'
import { OfficeInfoSection, ApplicantSection } from './inputParts'
import { MessageColumnSection } from '../../../layout/inputParts/MessageColumnSection'
import { mediaQuery } from '../../../../themes/size'
import { fetchAddress } from '../../../../util/fetchAddressByPostalCode'
import {
  isNotAllEmpty as isNotAllEmptyBase,
  isPhoneNumber,
  isPostalCode,
  isZenKatakana,
} from '../../../../util/validation'

type Props = {
  disabled: boolean
  shouldShowOtherFormLinks?: true
}

const Component: React.FC<Props> = (props) => {
  const methods = useFormContext<
    OfficeKaiinjouhouHenkoutodokeData['formData']
  >()
  const { control } = methods

  const handleChangePostalCode = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputPostalCode = e.target.value
    if (inputPostalCode.length === 8) {
      const address = await fetchAddress(inputPostalCode)
      if (address) {
        methods.setValue('address', address, { shouldDirty: true })
      }
    }
  }

  const isNotAllEmpty = isNotAllEmptyBase(methods, [
    'name',
    'nameKana',
    'representativeName',
    'representativeNameKana',
    'clerkName',
    'clerkNameKana',
    'postalCode',
    'address',
    'tel',
  ])

  return (
    <>
      <HeadingArea>
        <Heading type="screenTitle" tag="h1">
          ▼変更届 - 事業所情報
        </Heading>
      </HeadingArea>

      {Object.keys(methods.errors).length > 0 && (
        <Section>
          <ErrorMessage>
            入力内容に不備があります。各入力項目のメッセージを参照してください。
          </ErrorMessage>
        </Section>
      )}

      <OfficeInfoSection
        shouldShowOtherFormLinks={props.shouldShowOtherFormLinks}
      />

      <ApplicantSection disabled={props.disabled} />

      <Section>
        <Heading type="sectionTitle" tag="h2">
          ■申請情報（事業所情報）
        </Heading>
        <Paragraph>※変更点のみご入力ください。</Paragraph>
        {methods.errors.name?.type === 'isNotAllEmpty' && (
          <ErrorMessage>一つ以上の項目をご入力ください。</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            事業所名
          </Heading>
          <Controller
            control={control}
            name="name"
            rules={{ validate: { isNotAllEmpty } }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('name', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        <InputField>
          <Heading type="blockTitle" tag="span">
            フリガナ
            <br />
            (全角カナ)
          </Heading>
          <Controller
            control={control}
            name="nameKana"
            rules={{
              validate: { isZenKatakana, isNotAllEmpty },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('nameKana', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {methods.errors.nameKana?.type === 'isZenKatakana' && (
          <ErrorMessage>全角カナで入力してください</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            代表氏名
          </Heading>
          <Controller
            control={control}
            name="representativeName"
            rules={{ validate: { isNotAllEmpty } }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('representativeName', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        <InputField>
          <Heading type="blockTitle" tag="span">
            フリガナ
            <br />
            (全角カナ)
          </Heading>
          <Controller
            control={control}
            name="representativeNameKana"
            rules={{
              validate: { isZenKatakana, isNotAllEmpty },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('representativeNameKana', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {methods.errors.representativeNameKana?.type === 'isZenKatakana' && (
          <ErrorMessage>全角カナで入力してください</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            事務担当者
          </Heading>
          <Controller
            control={control}
            name="clerkName"
            rules={{ validate: { isNotAllEmpty } }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('clerkName', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        <InputField>
          <Heading type="blockTitle" tag="span">
            フリガナ
            <br />
            (全角カナ)
          </Heading>
          <Controller
            control={control}
            name="clerkNameKana"
            rules={{
              validate: { isZenKatakana, isNotAllEmpty },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('clerkNameKana', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {methods.errors.clerkNameKana?.type === 'isZenKatakana' && (
          <ErrorMessage>全角カナで入力してください</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            事業所郵便番号
            <br />
            (半角数字+半角ハイフン)
          </Heading>
          <Controller
            control={control}
            name="postalCode"
            rules={{
              validate: { isPostalCode, isNotAllEmpty },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('postalCode', undefined)
                  }
                  handleChangePostalCode(e)
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {methods.errors.postalCode?.type === 'isPostalCode' && (
          <ErrorMessage>半角数字+半角ハイフンで入力してください</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            所在地
          </Heading>
          <Controller
            control={control}
            name="address"
            rules={{ validate: { isNotAllEmpty } }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                value={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('address', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        <InputField>
          <Heading type="blockTitle" tag="span">
            電話番号
            <br />
            (半角数字)
          </Heading>
          <Controller
            control={control}
            name="tel"
            rules={{
              validate: { isPhoneNumber, isNotAllEmpty },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={(e) => {
                  onChange(e)
                  if (e.target.value.length === 0) {
                    methods.setValue('tel', undefined)
                  }
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {methods.errors.tel?.type === 'isPhoneNumber' && (
          <ErrorMessage>半角数字10桁か11桁で入力してください</ErrorMessage>
        )}
        <InputFieldFourRadio>
          <Heading type="blockTitle" tag="span">
            会費の振替方法
          </Heading>
          <Controller
            control={control}
            name="paymentMethod"
            rules={{ validate: isNotAllEmpty }}
            defaultValue={'noChange'}
            render={({ onChange, value }) => (
              <RadioButtonWrapper>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="変更なし"
                    value="noChange"
                    checked={value === 'noChange'}
                    onChange={onChange}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="現金"
                    value="cache"
                    checked={value === 'cache'}
                    onChange={onChange}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="口座"
                    value="withdrawal"
                    checked={value === 'withdrawal'}
                    onChange={onChange}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
                <RadioButtonArea>
                  <RadioButtonLabel
                    label="振込"
                    value="transfer"
                    checked={value === 'transfer'}
                    onChange={onChange}
                    disabled={props.disabled}
                  />
                </RadioButtonArea>
              </RadioButtonWrapper>
            )}
          />
        </InputFieldFourRadio>
        <Paragraph>
          ※現金でのお支払い /
          口座振替（口座振替の場合は所定の用紙をお送りします。）
        </Paragraph>
      </Section>

      <MessageColumnSection disabled={props.disabled} />
    </>
  )
}

const HeadingArea = styled.div`
  margin-top: 32px;
`
export const Section = styled.section`
  margin-top: 72px;
`
export const InputField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 510px;
  margin-top: 16px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
    width: 100%;

    & > span {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }
`
const Paragraph = styled.p`
  margin-top: 24px;
  font-size: 16px;
`
const RadioButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
  }
`
const RadioButtonArea = styled.div`
  display: flex;
  align-items: center;
  width: 110px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    margin-top: 8px;
  }
`
const InputFieldFourRadio = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 576px;
  margin-top: 16px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
    width: 100%;

    & > span {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }
`
const ErrorMessage = styled.div`
  color: red;
`
export default Component
